import React, { useMemo } from 'react';

import Debug from '@/components/Debug';
import RenderRichText from '@/components/RenderRichText';
import { Color } from '@/constants/color';
import { ExternalVerificationSource } from '@/types/externalVerification';
import Accordion from '@/ui/components/Accordion';
import Button from '@/ui/components/Button';
import DataList, { DataRow } from '@/ui/components/DataList';
import Heading from '@/ui/components/Heading';
import { currencyInCentsFormatter } from '@/utils/currencies';
import formatStaffName from '@/utils/formatStaffName';

import ExternalPolicyVerification from '../ExternalPolicyVerification';

import type { Props } from './types';

const InsuranceVOB = ({
  insuranceVerification,
  onReVerifyPolicy,
  canRunVob,
  headerText
}: Props) => {
  const {
    status,
    verified_at,
    verified_through,
    verified_by,
    verified_via,
    deductible_remaining,
    out_of_pocket_maximum_remaining_in_cents,
    vob_notes,
    external_policy_verification,
    service_code,
    used_policyholder_info
  } = insuranceVerification;
  const verifiedBy = verified_by ? formatStaffName({ staff: verified_by }) : '-';

  const verificationSource = useMemo(() => {
    if (typeof verified_via === 'string') {
      return verified_via;
    }

    return `${verified_via?.name || '-'}`;
  }, [verified_via]);

  const oopMaxRemaining =
    out_of_pocket_maximum_remaining_in_cents || out_of_pocket_maximum_remaining_in_cents === 0
      ? currencyInCentsFormatter(out_of_pocket_maximum_remaining_in_cents)
      : '-';

  const deductibleRemaining =
    deductible_remaining || deductible_remaining === 0
      ? currencyInCentsFormatter(deductible_remaining)
      : '-';

  const estimatedMonthlyCost = insuranceVerification?.estimated_monthly_cost_in_cents
    ? currencyInCentsFormatter(insuranceVerification.estimated_monthly_cost_in_cents)
    : '-';

  const estimatedInitialMonthCost = insuranceVerification?.estimated_initial_month_cost_in_cents
    ? currencyInCentsFormatter(insuranceVerification.estimated_initial_month_cost_in_cents)
    : '-';
  const coPay =
    external_policy_verification?.finance?.office_copay_in_cents != null
      ? currencyInCentsFormatter(external_policy_verification.finance.office_copay_in_cents)
      : '-';

  const coInsurance =
    external_policy_verification?.finance?.coinsurance_percentage != null
      ? `${external_policy_verification.finance.coinsurance_percentage}%`
      : '-';

  return (
    <div className="flex flex-col gap-2 pt-4">
      {headerText && <Heading value={3} text={headerText} />}
      <div className="relative border-solid border border-gray-200 rounded-lg p-2">
        <DataList className="[&>div]:flex [&>div]:flex-row [&>div]:gap-2">
          <DataRow label="Status:">{status}</DataRow>
          <DataRow label="Date:">
            {verified_at ? new Date(verified_at).toISOString().split('T')[0] : '-'}
          </DataRow>
          <DataRow label="Until:">
            {verified_through ? new Date(verified_through).toISOString().split('T')[0] : '-'}
          </DataRow>
          <DataRow label="Verified By:">{verifiedBy}</DataRow>
          <DataRow label="Verification Source:">{verificationSource}</DataRow>
          <DataRow label="Service Code:">{service_code ?? '-'}</DataRow>
          {verificationSource === ExternalVerificationSource.FRONTRUNNER && (
            <DataRow label="Used policyholder information:">
              {used_policyholder_info ? 'Yes' : 'No'}
            </DataRow>
          )}
          <hr />
          <DataRow label="Co-pay:">{coPay}</DataRow>
          <DataRow label="Co-insurance:">{coInsurance}</DataRow>
          <DataRow label="OOP Max remaining:">{oopMaxRemaining}</DataRow>
          <DataRow label="Estimated Initial Month Cost:">{estimatedInitialMonthCost}</DataRow>
          <DataRow label="Estimated Monthly Cost:">{estimatedMonthlyCost}</DataRow>
          <DataRow label="Deductible remaining:">{deductibleRemaining}</DataRow>
        </DataList>
        {vob_notes && (
          <DataList className="[&>div]:flex [&>div]:flex-col [&>div]:items-start">
            <DataRow label="Notes:">
              <RenderRichText className="prose dark:prose-invert prose-sm" body={vob_notes} />
            </DataRow>
          </DataList>
        )}
        {external_policy_verification && (
          <Accordion buttonText="External verification">
            <ExternalPolicyVerification externalPolicyVerification={external_policy_verification} />
          </Accordion>
        )}
        <Debug object={insuranceVerification} />
      </div>

      {canRunVob && (
        <Button
          ariaLabel="Re-Verify this policy"
          onClick={onReVerifyPolicy}
          color={Color.WHITE}
          size="sm"
          className="flex-1">
          Re-Verify this policy
        </Button>
      )}
    </div>
  );
};
export default React.memo(InsuranceVOB);
